import type { FetchRequestError } from "@lib/errors";
import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import sharedConstants from "@lib/shared_constants";
import type { SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES } from "@lib/shared_constants";

export const knowledgeSourceToolType = "knowledge_source" as const;
export const workflowAttributeSetterToolType = "workflow_attribute_setter" as const;
export const workflowAttributeGetterToolType = "workflow_attribute_getter" as const;
export const logCustomEventToolType = "log_custom_event" as const;
export const setCustomAttributeToolType = "set_custom_attribute" as const;
export const getCustomAttributeToolType = "get_custom_attribute" as const;
export const getUserAttributeToolType = "get_user_attribute" as const;
export const callWebhookToolType = "call_webhook" as const;
export const getWebhookResponseToolType = "get_webhook_response" as const;

export interface KnowledgeSourceToolConfig {
  tool_type: typeof knowledgeSourceToolType;
  definition_id: string;
}

export interface WorkflowAttributeSetterToolConfig {
  tool_type: typeof workflowAttributeSetterToolType;
  definition_id: string;
}

export interface WorkflowAttributeGetterToolConfig {
  tool_type: typeof workflowAttributeGetterToolType;
  definition_id: string;
}

export interface LogCustomEventToolConfig {
  tool_type: typeof logCustomEventToolType;
  definition_id: string;
}

export interface SetCustomAttributeToolConfig {
  tool_type: typeof setCustomAttributeToolType;
  definition_id: string;
}

export interface GetCustomAttributeToolConfig {
  tool_type: typeof getCustomAttributeToolType;
  marker_id: string;
  attribute_name: string;
}

export interface GetUserAttributeToolConfig {
  tool_type: typeof getUserAttributeToolType;
  marker_id: string;
  attribute_name: string;
}

export interface CallWebhookToolConfig {
  tool_type: typeof callWebhookToolType;
  definition_id: string;
}

export interface GetWebhookResponseToolConfig {
  tool_type: typeof getWebhookResponseToolType;
  marker_id: string;
  definition_id: string;
  response_field_name: string;
}

export type ToolConfig =
  | KnowledgeSourceToolConfig
  | WorkflowAttributeSetterToolConfig
  | WorkflowAttributeGetterToolConfig
  | LogCustomEventToolConfig
  | SetCustomAttributeToolConfig
  | GetCustomAttributeToolConfig
  | GetUserAttributeToolConfig
  | CallWebhookToolConfig
  | GetWebhookResponseToolConfig;

export interface ConversationalWorkflowStep {
  instruction: string;
  tool_configs: ToolConfig[];
}

export interface OperatorSafeConversationalWorkflowStep {
  instruction: string;
}

export interface ConversationalWorkflowChatSetting {
  app_ids: string[];
}

export interface ConversationalWorkflowMessagingChannelSetting {
  subscription_group_ids: string[];
}

/** `draft` is saved but never routed to; `active` answers customers on its apps and subscription groups. */
export type ConversationalWorkflowStatus = "draft" | "active";

export interface ConversationalWorkflowResultObject {
  id: string;
  name: string;
  purpose: string;
  status: ConversationalWorkflowStatus;
  segment_ids: string[];
  steps: ConversationalWorkflowStep[];
  chat_setting: ConversationalWorkflowChatSetting | null;
  messaging_channel_setting: ConversationalWorkflowMessagingChannelSetting | null;
  created_by_email: string;
  created_at: string;
  updated_at: string;
  territory_ids: string[];
  tags: string[];
}

export interface ConversationalWorkflowResult {
  result: ConversationalWorkflowResultObject;
}

export interface OperatorSafeConversationalWorkflowResultObject {
  id: string;
  name: string;
  purpose: string;
  status: ConversationalWorkflowStatus;
  step_count: number;
  steps: OperatorSafeConversationalWorkflowStep[];
}

export interface OperatorSafeConversationalWorkflowResult {
  result: OperatorSafeConversationalWorkflowResultObject;
}

export type GetConversationalWorkflowResult =
  | ConversationalWorkflowResult
  | OperatorSafeConversationalWorkflowResult;

export interface GetConversationalWorkflowProps {
  appGroupId: string;
  workflowId: string;
}

export interface ConversationalWorkflowPayload {
  id?: string;
  name: string;
  purpose: string;
  segment_ids: string[];
  steps: ConversationalWorkflowStep[];
  chat_setting: ConversationalWorkflowChatSetting;
  messaging_channel_setting: ConversationalWorkflowMessagingChannelSetting;
  // Sent only while workflows carry teams; a save without them keeps the workflow's teams and tags.
  territory_ids?: string[];
  tag_names?: string[];
  /** Omitted leaves the stored status alone; a new workflow starts as a draft. */
  status?: ConversationalWorkflowStatus;
}

export interface PostConversationalWorkflowProps extends ConversationalWorkflowPayload {
  appGroupId: string;
}

export interface PutConversationalWorkflowProps extends ConversationalWorkflowPayload {
  appGroupId: string;
  workflowId: string;
  /** The status the page loaded with; the save is refused with STATUS_CHANGED when the stored one differs. */
  expected_status?: ConversationalWorkflowStatus;
}

export interface DeleteConversationalWorkflowProps {
  appGroupId: string;
  workflowId: string;
}

export type DeleteConversationalWorkflowInUseError = FetchUserEncodedError<
  SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["DELETE_IN_USE"]
>;

export type DeleteConversationalWorkflowLockTimeoutError = FetchUserEncodedError<
  SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["LOCK_TIMEOUT"]
>;

export type DeleteConversationalWorkflowErrors =
  | DeleteConversationalWorkflowInUseError
  | DeleteConversationalWorkflowLockTimeoutError;

export const isDeleteConversationalWorkflowInUseError = (
  error: FetchRequestError
): error is DeleteConversationalWorkflowInUseError => {
  return (
    error instanceof FetchUserEncodedError &&
    error.errorCode === sharedConstants.CONVERSATIONAL_WORKFLOW.ERROR_CODES.DELETE_IN_USE
  );
};

export const isDeleteConversationalWorkflowLockTimeoutError = (
  error: FetchRequestError
): error is DeleteConversationalWorkflowLockTimeoutError => {
  return (
    error instanceof FetchUserEncodedError &&
    error.errorCode === sharedConstants.CONVERSATIONAL_WORKFLOW.ERROR_CODES.LOCK_TIMEOUT
  );
};

export interface PostConversationalPreviewWorkflowProps {
  appGroupId: string;
  sessionId: string;
  userMessage: string;
  userId: string;
  conversationalWorkflowPayload: ConversationalWorkflowPayload;
}

export type PostConversationalPreviewWorkflowErrors =
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["SESSION_ID_REQUIRED"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["USER_MESSAGE_REQUIRED"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["USER_ID_REQUIRED"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["USER_ID_INVALID"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["ID_REQUIRED"]>
  | FetchUserEncodedError<SHARED_CONSTANTS_CONVERSATIONAL_WORKFLOW_ERROR_CODES["NOT_FOUND"]>;

export const isPostConversationalPreviewWorkflowError = (
  error: FetchRequestError
): error is PostConversationalPreviewWorkflowErrors => {
  return error instanceof FetchUserEncodedError;
};

export interface PostConversationalPreviewWorkflowResult {
  message_id: string;
}
